import React, { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { useLang, UI } from "../i18n";
import { getProject, TRAVEL, FICHE, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { MiniSite, CourtLines, MonoTag, GhostText, Stamp } from "../components/motifs";
import { FicheInfo } from "../components/Fiche";
import { Lightbox, WhatsAppShare } from "../components/Lightbox";
import { ContactSection } from "../components/Layout";

const GALLERY = [IMG.team1, IMG.courtAction, IMG.team2, IMG.heroA];

const EB_TARGET = new Date("2026-10-15T23:59:59");
const ebCalc = () => {
  const d = Math.max(0, EB_TARGET - new Date());
  return [Math.floor(d / 864e5), Math.floor(d / 36e5) % 24, Math.floor(d / 6e4) % 60, Math.floor(d / 1e3) % 60];
};

const EarlyBird = ({ e, th, t }) => {
  const [vals, setVals] = useState(ebCalc);
  useEffect(() => {
    const id = setInterval(() => setVals(ebCalc()), 1000);
    return () => clearInterval(id);
  }, []);
  const labels = [
    { fr: "Jours", en: "Days", es: "Días" },
    { fr: "Heures", en: "Hours", es: "Horas" },
    { fr: "Minutes", en: "Minutes", es: "Minutos" },
    { fr: "Secondes", en: "Seconds", es: "Segundos" },
  ];
  return (
  <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="winter-cup-pricing">
    <Reveal>
      <div className="rounded-lg border p-8 md:p-10" style={{ backgroundColor: "var(--ms-surface)", borderColor: `${th.accent}55`, borderTop: `3px solid ${th.accent}` }}>
        <MonoTag className="blink" style={{ color: th.accent }}>
          ● {t({ fr: "Offre limitée", en: "Limited offer", es: "Oferta limitada" })}
        </MonoTag>
        <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-3">{t(e.pricing.title)}</h2>
        {vals.some((v) => v > 0) && (
        <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="earlybird-countdown">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
            {t({ fr: "Fin de l'offre dans", en: "Offer ends in", es: "La oferta termina en" })}
          </span>
          <div className="flex gap-2 md:gap-3">
            {vals.map((v, i) => (
              <div key={i} data-testid={`eb-count-${i}`} className="rounded-md border border-white/10 bg-black/30 px-3 py-2 md:px-4 md:py-3 text-center min-w-[58px] md:min-w-[72px]">
                <div className="font-display text-2xl md:text-3xl" style={{ color: th.accent }}>{String(v).padStart(2, "0")}</div>
                <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mt-1">{t(labels[i])}</div>
              </div>
            ))}
          </div>
        </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {e.pricing.rows.map((r) => (
            <div key={r.label.fr} className="rounded-md border border-white/10 p-6"
              style={r.early ? { borderColor: `${th.accent}66`, backgroundColor: `${th.accent}11` } : {}}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: r.early ? th.accent : "rgba(255,255,255,0.5)" }}>
                {t(r.label)}
              </p>
              <div className="flex items-baseline gap-4 mt-4">
                <div>
                  <div className="font-display text-3xl md:text-4xl">{r.ext}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">{t({ fr: "Externat", en: "Day rate", es: "Externado" })}</div>
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl" style={{ color: th.accent }}>{r.int}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">{t({ fr: "Internat", en: "Boarding", es: "Internado" })}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="font-body text-white/80 mt-6 flex items-center gap-2">
          <span className="rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-black" style={{ backgroundColor: th.accent }}>€</span>
          {t(e.pricing.note)}
        </p>
      </div>
    </Reveal>
  </section>
  );
};

// ÉVÉNEMENTS — single flagship event: Winter Cup (its own mini-site)
export default function Evenements() {
  useLenis();
  const { t } = useLang();
  const [lb, setLb] = useState(null);
  const e = getProject("winter-cup");
  const th = e.theme;
  const media = [...e.flyers, ...e.photos, ...e.venueInfo.photos].map((m) => m.src);
  return (
    <MiniSite theme={th}>
      <section data-testid="event-winter-cup" className="relative overflow-hidden pt-28 md:pt-40 pb-16 md:pb-24 grain">
        <GhostText text="WINTER" className="text-[20vw] top-16 -left-2" />
        <CourtLines className="-right-52 -top-40 w-[640px] h-[640px]" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">
                <CalendarDays size={12} className="inline mr-2 -mt-0.5" />{t(e.tag)}
              </MonoTag>
              <MonoTag className="blink border rounded-full px-3 py-1.5" style={{ color: th.accent, borderColor: `${th.accent}66` }}>
                {t({ fr: "Places limitées", en: "Limited spots", es: "Plazas limitadas" })} · 16 {t({ fr: "équipes", en: "teams", es: "equipos" })}
              </MonoTag>
            </div>
            <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.85] mt-5">{e.name}</h1>
            <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] mt-5" style={{ color: th.accent }}>
              {t(e.ages)} · {t(e.duration)} · 18 DEC 2026
            </p>
            <p className="font-body text-white/75 mt-5 max-w-2xl text-base md:text-lg">{t(e.desc)}</p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#contact" data-testid="event-cta-winter-cup"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                {t({ fr: "S'inscrire au tournoi", en: "Register for the tournament", es: "Inscribirse al torneo" })} <ArrowUpRight size={18} />
              </a>
              <WhatsAppShare testid="event-share-winter-cup" text="Winter Cup — Air Sports Travel"
                label={t({ fr: "Partager", en: "Share", es: "Compartir" })} />
            </div>
          </div>
          <Reveal className="lg:col-span-5 flex justify-center relative">
            <div className="relative">
              <img src={e.logo} alt="Winter Cup" data-testid="event-logo-winter-cup"
                className="w-56 md:w-80 rounded-2xl bg-white p-3 shadow-2xl -rotate-3" />
              <Stamp top="OFFICIAL EVENT" bottom="DEC" className="absolute -bottom-8 -right-4" color={th.accent} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="winter-cup-venue">
        <Reveal>
          <div className="rounded-lg border border-white/10 overflow-hidden grid grid-cols-1 lg:grid-cols-12" style={{ backgroundColor: "var(--ms-surface)" }}>
            <div className="lg:col-span-7 p-8 md:p-10">
              <MonoTag className="blink" style={{ color: th.accent }}>
                ★ {t({ fr: "Nouveau lieu cette année", en: "New venue this year", es: "Nuevo lugar este año" })} ★
              </MonoTag>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] mt-4">Athletica</h2>
              <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] mt-3" style={{ color: th.accent }}>
                {t(e.venueInfo.city)}
              </p>
              <p className="font-body text-white/80 mt-5 max-w-2xl">{t(e.venueInfo.intro)}</p>
              <ul className="mt-6 space-y-3">
                {e.venueInfo.points.map((pt) => (
                  <li key={pt.fr} className="flex items-start gap-3 font-body text-white/85">
                    <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: th.accent, boxShadow: `0 0 8px ${th.accent}` }} />
                    {t(pt)}
                  </li>
                ))}
              </ul>
              <a href={e.venueInfo.url} target="_blank" rel="noopener noreferrer" data-testid="venue-link"
                className="inline-flex items-center gap-2 mt-7 font-heading uppercase text-sm tracking-wide border rounded-full px-6 py-3 hover:bg-white/10 transition-colors duration-300"
                style={{ borderColor: `${th.accent}66`, color: th.accent }}>
                {t({ fr: "Découvrir la salle", en: "Discover the venue", es: "Descubrir la sala" })} <ArrowUpRight size={16} />
              </a>
            </div>
            <div className="lg:col-span-5 grid grid-rows-[auto_1fr]">
              <div className="p-8 md:p-10 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col justify-center gap-6">
                {[
                  { n: "3", l: { fr: "Terrains sur place", en: "Courts on site", es: "Canchas en el lugar" } },
                  { n: "550 €", l: { fr: "Internat — chambre sur place ou < 30 min à pied", en: "Boarding — room on site or < 30-min walk", es: "Internado — habitación en el lugar o < 30 min a pie" } },
                  { n: "USA", l: { fr: "Centre d'entraînement de la Team USA", en: "Team USA training center", es: "Centro de entrenamiento del Team USA" } },
                ].map((s) => (
                  <div key={s.n} className="flex items-center gap-5">
                    <div className="font-display text-4xl md:text-5xl" style={{ color: th.accent }}>{s.n}</div>
                    <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-white/60 max-w-[220px]">{t(s.l)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5" data-testid="venue-photos">
            {e.venueInfo.photos.map((ph, pi) => (
              <button key={ph.src} type="button" data-testid={`venue-photo-${pi}`}
                onClick={() => setLb(e.flyers.length + e.photos.length + pi)}
                className="group/v text-left focus:outline-none focus:ring-2 focus:ring-white/40 rounded-md">
                <div className="relative overflow-hidden rounded-md border border-white/10" style={{ aspectRatio: "4 / 3" }}>
                  <img src={ph.src} alt={t(ph.cap)} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover/v:scale-105 transition-transform duration-500" />
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-2">{t(ph.cap)}</p>
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <EarlyBird e={e} th={th} t={t} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="winter-cup-flyers">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t({ fr: "Les affiches officielles", en: "Official flyers", es: "Carteles oficiales" })}</MonoTag>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-6">
            {e.flyers.map((f, fi) => (
              <button key={f.src} type="button" data-testid={`wc-flyer-${fi}`} onClick={() => setLb(fi)}
                className="group/f text-left focus:outline-none focus:ring-2 focus:ring-white/40 rounded-md">
                <div className="relative overflow-hidden rounded-md border border-white/10" style={{ aspectRatio: "2 / 3" }}>
                  <img src={f.src} alt={t(f.cap)} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover/f:scale-105 transition-transform duration-500" />
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-3">{t(f.cap)}</p>
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="winter-cup-video">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t({ fr: "La vidéo de la Winter Cup", en: "The Winter Cup video", es: "El vídeo de la Winter Cup" })}</MonoTag>
          <div className="mt-5 relative overflow-hidden rounded-lg border border-white/10 w-full" style={{ aspectRatio: "16 / 9" }}>
            <iframe src={e.video} title="The Winter Cup — video" data-testid="winter-cup-video-iframe"
              className="absolute inset-0 w-full h-full" frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen />
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="winter-cup-categories">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t({ fr: "Catégories · Athletica, Eaubonne (95)", en: "Categories · Athletica, Eaubonne (95)", es: "Categorías · Athletica, Eaubonne (95)" })}</MonoTag>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-3">
            {t({ fr: "4 catégories cette saison", en: "4 categories this season", es: "4 categorías esta temporada" })}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {e.categories.map((c) => (
              <div key={c.label.fr} data-testid={`wc-category-${c.label.fr}`}
                className="relative rounded-md border border-white/10 p-6 pt-8 text-center"
                style={{ backgroundColor: "var(--ms-surface)", borderTop: c.isNew ? `3px solid ${th.accent}` : "1px solid rgba(255,255,255,0.1)" }}>
                {c.isNew && (
                  <MonoTag className="blink absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-black font-semibold"
                    style={{ backgroundColor: th.accent }}>
                    {t({ fr: "NOUVEAU", en: "NEW", es: "NUEVO" })}
                  </MonoTag>
                )}
                <div className="font-display text-2xl md:text-3xl uppercase">{t(c.label)}</div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-3">
                  {t({ fr: "26 → 30 déc · Paris", en: "Dec 26 → 30 · Paris", es: "26 → 30 dic · París" })}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Reveal><FicheInfo fiche={e} accent={th.accent} /></Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t(FICHE.objectives)}</MonoTag>
          <ul className="mt-6 space-y-4" data-testid="event-objectives">
            {e.objectives.map((o, i) => (
              <li key={i} className="flex gap-4 items-start rounded-md p-5 border border-white/10"
                style={{ backgroundColor: "var(--ms-surface)" }}>
                <span className="font-display text-xl" style={{ color: th.accent }}>0{i + 1}</span>
                <span className="font-body text-white/85">{t(o)}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <MonoTag style={{ color: th.accent }}>{t({ fr: "Galerie", en: "Gallery", es: "Galería" })}</MonoTag>
          <div className="grid grid-cols-2 gap-3 mt-6">
            {e.photos.map((g, gi) => (
              <button key={g.src} type="button" data-testid={`gallery-winter-cup-${gi}`}
                onClick={() => setLb(e.flyers.length + gi)}
                className="group/g overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-white/40">
                <img src={g.src} alt={t(g.cap)} className="w-full h-44 md:h-56 object-cover group-hover/g:scale-110 transition-transform duration-500" />
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {lb !== null && (
        <Lightbox images={media} index={lb} onIndex={setLb} onClose={() => setLb(null)} />
      )}
      <ContactSection defaultProgram="Winter Cup" accent={th.accent} />
    </MiniSite>
  );
}
